import { Chain } from '@ankr.com/chains-list';
import { MouseEvent, useCallback, useMemo } from 'react';

import { IProjectWithBlockchains } from 'domains/projects/types';
import { JWT } from 'domains/jwtToken/types';
import { hasAllBlockchainsInWhitelist } from 'modules/projects/utils/hasAllBlockchainsInWhitelist';
import { selectAllPathsByChainId } from 'modules/chains/store/selectors';
import { useAppSelector } from 'store/useAppSelector';
import { useMenu } from 'modules/common/hooks/useMenu';

export interface IUsePrivateChainCardProps {
  chain: Chain;
  hasPremium: boolean;
  projects: JWT[];
  projectsWithBlockchains: IProjectWithBlockchains[];
}

export const usePrivateChainCard = ({
  chain,
  hasPremium,
  projects,
  projectsWithBlockchains,
}: IUsePrivateChainCardProps) => {
  const allChainPaths = useAppSelector(state =>
    selectAllPathsByChainId(state, chain.id),
  );

  const isEndpointLocked = Boolean(chain.premiumOnly && !hasPremium);

  const chainProjects = useMemo(() => {
    return (
      projectsWithBlockchains?.filter(project => {
        const blockchains = project.blockchains ?? [];

        if (hasAllBlockchainsInWhitelist(blockchains)) {
          return true;
        }

        const isCurrentPathIncluded =
          allChainPaths?.length > 0 &&
          project.blockchains.length > 0 &&
          allChainPaths.some(path => project.blockchains.includes(path));

        return isCurrentPathIncluded;
      }) ?? []
    );
  }, [allChainPaths, projectsWithBlockchains]);

  const filteredJwtTokens = useMemo(
    () =>
      projects.filter(
        token =>
          !chainProjects?.every(
            project =>
              project.userEndpointToken.toLowerCase() !==
              token.userEndpointToken.toLowerCase(),
          ),
      ),
    [projects, chainProjects],
  );

  const { anchorEl, handleClose, handleOpen, open } = useMenu();

  const handleOpenChainMenu = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      handleOpen(e);
    },
    [handleOpen],
  );

  const hasChainProjects = chainProjects.length > 0;

  return {
    anchorEl,
    chainProjects,
    filteredJwtTokens,
    handleClose,
    handleOpenChainMenu,
    hasChainProjects,
    isEndpointLocked,
    isMenuOpened: open,
  };
};
